/**
 * Service de recherche de nom d'utilisateur inspiré de Sherlock et RPUC
 * Implémente la détection intelligente des profils avec minimisation des faux positifs
 */

import { apiService } from './api';

// Interface pour définir une plateforme sociale
interface SocialPlatform {
  name: string;
  url: string;
  urlMain: string;
  errorType: 'status_code' | 'response_url' | 'message';
  errorCode?: number;
  errorMsg?: string;
  regexCheck?: string;
  negate?: boolean;
  headers?: Record<string, string>;
  request_method?: 'GET' | 'POST' | 'HEAD';
  request_payload?: Record<string, any>;
  response_url?: string;
  isNSFW?: boolean;
  tags?: string[];
}

// Base de données des plateformes inspirée de Sherlock
const SOCIAL_PLATFORMS: Record<string, SocialPlatform> = {
  "Instagram": {
    name: "Instagram",
    url: "https://instagram.com/{}",
    urlMain: "https://instagram.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.]{1,30}$",
    tags: ["social", "photo"]
  },
  "Twitter": {
    name: "Twitter", 
    url: "https://twitter.com/{}",
    urlMain: "https://twitter.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_]{1,15}$",
    tags: ["social", "microblog"]
  },
  "GitHub": {
    name: "GitHub",
    url: "https://github.com/{}",
    urlMain: "https://github.com/",
    errorType: "status_code", 
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9-]{1,39}$",
    tags: ["code", "professional"]
  },
  "LinkedIn": {
    name: "LinkedIn",
    url: "https://linkedin.com/in/{}",
    urlMain: "https://linkedin.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9-]{3,100}$",
    tags: ["professional", "social"]
  },
  "Reddit": {
    name: "Reddit",
    url: "https://reddit.com/user/{}",
    urlMain: "https://reddit.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_-]{3,20}$",
    tags: ["social", "forum"]
  },
  "Facebook": {
    name: "Facebook",
    url: "https://facebook.com/{}",
    urlMain: "https://facebook.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9.]{5,50}$",
    tags: ["social"]
  },
  "TikTok": {
    name: "TikTok",
    url: "https://tiktok.com/@{}",
    urlMain: "https://tiktok.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.]{1,24}$",
    tags: ["social", "video"]
  },
  "YouTube": {
    name: "YouTube",
    url: "https://youtube.com/@{}",
    urlMain: "https://youtube.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.-]{1,100}$",
    tags: ["video", "social"]
  },
  "Snapchat": {
    name: "Snapchat",
    url: "https://snapchat.com/add/{}",
    urlMain: "https://snapchat.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.-]{1,15}$",
    tags: ["social", "ephemeral"]
  },
  "Pinterest": {
    name: "Pinterest",
    url: "https://pinterest.com/{}",
    urlMain: "https://pinterest.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_]{3,30}$",
    tags: ["social", "visual"]
  },
  "Telegram": {
    name: "Telegram",
    url: "https://t.me/{}",
    urlMain: "https://telegram.org/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_]{5,32}$",
    tags: ["messaging"]
  },
  "Discord": {
    name: "Discord",
    url: "https://discord.com/users/{}",
    urlMain: "https://discord.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[0-9]{17,19}$",
    tags: ["gaming", "communication"]
  },
  "Twitch": {
    name: "Twitch",
    url: "https://twitch.tv/{}",
    urlMain: "https://twitch.tv/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_]{4,25}$",
    tags: ["gaming", "streaming"]
  },
  "Steam": {
    name: "Steam",
    url: "https://steamcommunity.com/id/{}",
    urlMain: "https://steamcommunity.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_]{3,32}$",
    tags: ["gaming"]
  },
  "Spotify": {
    name: "Spotify",
    url: "https://open.spotify.com/user/{}",
    urlMain: "https://spotify.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.-]{1,50}$",
    tags: ["music"]
  },
  "Medium": {
    name: "Medium",
    url: "https://medium.com/@{}",
    urlMain: "https://medium.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.-]{1,50}$",
    tags: ["blog", "writing"]
  },
  "DeviantArt": {
    name: "DeviantArt",
    url: "https://{}.deviantart.com",
    urlMain: "https://deviantart.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9-]{1,20}$",
    tags: ["art", "creative"]
  },
  "Behance": {
    name: "Behance",
    url: "https://behance.net/{}",
    urlMain: "https://behance.net/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_]{1,30}$",
    tags: ["design", "creative", "professional"]
  },
  "Dribbble": {
    name: "Dribbble",
    url: "https://dribbble.com/{}",
    urlMain: "https://dribbble.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_-]{1,20}$",
    tags: ["design", "creative"]
  },
  "GitLab": {
    name: "GitLab",
    url: "https://gitlab.com/{}",
    urlMain: "https://gitlab.com/",
    errorType: "status_code",
    errorCode: 404,
    regexCheck: "^[a-zA-Z0-9_.-]{1,255}$",
    tags: ["code", "professional"]
  }
};

// Interface pour le résultat de recherche
export interface UsernameSearchResult {
  platform: string;
  username: string;
  url: string;
  status: 'found' | 'not_found' | 'error' | 'checking';
  confidence: number; // 0-1, probabilité que ce soit un vrai profil
  response_time?: number;
  error_details?: string;
  additional_info?: {
    profile_image?: string;
    display_name?: string;
    bio?: string;
    followers?: number;
    verified?: boolean;
  };
}

// Interface pour les options de recherche
export interface SearchOptions {
  platforms?: string[]; // Plateformes spécifiques à rechercher
  includeNSFW?: boolean;
  timeout?: number; // Timeout par requête en ms
  maxConcurrent?: number; // Nombre max de requêtes simultanées
  includeMetadata?: boolean; // Récupérer des métadonnées du profil
}

// Interface pour la réponse de l'API
interface UsernameSearchResponse {
  results: UsernameSearchResult[];
  summary?: {
    total: number;
    found: number;
    notFound: number;
    errors: number;
    highConfidence: number;
    averageResponseTime: number;
    topPlatforms: string[];
  };
}

// Interface pour la réponse des plateformes
interface PlatformResponse {
  name: string;
  category: string;
}

class UsernameSearchService {
  private readonly defaultTimeout = 30000; // 30 secondes pour éviter les timeouts
  private readonly defaultMaxConcurrent = 5;
  private readonly userAgent = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36';

  /**
   * Valide si un nom d'utilisateur est compatible avec une plateforme
   */
  private validateUsername(username: string, platform: SocialPlatform): boolean {
    if (platform.regexCheck) {
      const regex = new RegExp(platform.regexCheck);
      return regex.test(username);
    }
    return true;
  }

  /**
   * Analyse la réponse pour détecter si le profil existe
   */
  private analyzeResponse(
    response: Response, 
    responseText: string, 
    platform: SocialPlatform,
    requestedUrl: string
  ): { exists: boolean; confidence: number; details?: string } {
    
    // Vérification basée sur le code de statut
    if (platform.errorType === 'status_code') {
      const isError = response.status === (platform.errorCode || 404);
      
      if (platform.negate) {
        return { 
          exists: isError, 
          confidence: response.status === 200 ? 0.9 : 0.1,
          details: `Status: ${response.status}`
        };
      } else {
        return { 
          exists: !isError, 
          confidence: response.status === 200 ? 0.9 : 0.1,
          details: `Status: ${response.status}`
        };
      }
    }

    // Vérification basée sur l'URL de réponse (redirections)
    if (platform.errorType === 'response_url') {
      const finalUrl = response.url;
      const urlsMatch = finalUrl === requestedUrl;
      return { 
        exists: urlsMatch, 
        confidence: urlsMatch ? 0.8 : 0.2,
        details: `URL redirect: ${finalUrl}`
      };
    }

    // Vérification basée sur le contenu de la réponse
    if (platform.errorType === 'message' && platform.errorMsg) {
      const hasErrorMessage = responseText.includes(platform.errorMsg);
      return { 
        exists: !hasErrorMessage, 
        confidence: hasErrorMessage ? 0.1 : 0.7,
        details: `Content analysis`
      };
    }

    // Par défaut, considérer que le profil existe si pas d'erreur
    return { 
      exists: response.status === 200, 
      confidence: 0.6,
      details: 'Default analysis'
    };
  }

  /**
   * Effectue une recherche sur une plateforme spécifique
   */
  private async searchOnPlatform(
    username: string, 
    platformName: string, 
    platform: SocialPlatform,
    options: SearchOptions
  ): Promise<UsernameSearchResult> {
    
    const result: UsernameSearchResult = {
      platform: platformName,
      username,
      url: platform.url.replace('{}', username),
      status: 'checking',
      confidence: 0
    };

    try {
      // Valider le nom d'utilisateur pour cette plateforme
      if (!this.validateUsername(username, platform)) {
        return {
          ...result,
          status: 'error',
          confidence: 0,
          error_details: 'Username format invalid for this platform'
        };
      }

      const startTime = Date.now();
      
      // Préparer les headers
      const headers: HeadersInit = {
        'User-Agent': this.userAgent,
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
        'Accept-Language': 'en-US,en;q=0.5',
        'Accept-Encoding': 'gzip, deflate',
        'Connection': 'keep-alive',
        'Upgrade-Insecure-Requests': '1',
        ...platform.headers
      };

      // Effectuer la requête
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || this.defaultTimeout);

      const response = await fetch(result.url, {
        method: platform.request_method || 'GET',
        headers,
        signal: controller.signal,
        redirect: 'follow'
      });

      clearTimeout(timeoutId);
      
      const responseTime = Date.now() - startTime;
      const responseText = await response.text();

      // Analyser la réponse
      const analysis = this.analyzeResponse(response, responseText, platform, result.url);
      
      return {
        ...result,
        status: analysis.exists ? 'found' : 'not_found',
        confidence: analysis.confidence,
        response_time: responseTime,
        error_details: analysis.details
      };

    } catch (error) {
      return {
        ...result,
        status: 'error',
        confidence: 0,
        error_details: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Effectue une recherche de nom d'utilisateur via l'API backend
   */
  async searchUsername(username: string, options: SearchOptions = {}): Promise<UsernameSearchResult[]> {
    // Nettoyer le nom d'utilisateur
    const cleanUsername = username.trim().replace(/^@/, '');
    
    if (!cleanUsername) {
      throw new Error('Username cannot be empty');
    }

    try {
      const response = await apiService.osintSearch<UsernameSearchResponse>('/osint/search/username', {
        username: cleanUsername,
        platforms: options.platforms,
        timeout: options.timeout || this.defaultTimeout,
        maxConcurrent: options.maxConcurrent || this.defaultMaxConcurrent,
        includeMetadata: options.includeMetadata || false
      });

      // Convertir les résultats backend vers le format frontend
      const results: UsernameSearchResult[] = response.data.results.map((result: any) => ({
        platform: result.platform,
        username: result.username,
        url: result.url,
        status: result.status,
        confidence: result.confidence,
        response_time: result.response_time,
        error_details: result.error_details,
        additional_info: result.profile_data
      }));

      return results;
    } catch (error) {
      console.error('Erreur recherche OSINT:', error);
      throw error;
    }
  }

  /**
   * Obtient la liste des plateformes disponibles via l'API backend
   */
  async getAvailablePlatforms(): Promise<Array<{ name: string; tags: string[]; isNSFW: boolean }>> {
    try {
      const response = await apiService.get<PlatformResponse[]>('/osint/platforms');
      
      return response.data.map((platform: PlatformResponse) => ({
        name: platform.name,
        tags: [platform.category],
        isNSFW: false
      }));
    } catch (error) {
      console.error('Erreur récupération plateformes:', error);
      // Fallback vers les plateformes locales
      return Object.entries(SOCIAL_PLATFORMS).map(([name, platform]) => ({
        name,
        tags: platform.tags || [],
        isNSFW: platform.isNSFW || false
      }));
    }
  }

  /**
   * Filtre les plateformes par tag
   */
  getPlatformsByTag(tag: string): string[] {
    return Object.entries(SOCIAL_PLATFORMS)
      .filter(([, platform]) => platform.tags?.includes(tag))
      .map(([name]) => name);
  }

  /**
   * Génère un rapport de résumé des résultats
   */
  generateSummaryReport(results: UsernameSearchResult[]): {
    total: number;
    found: number;
    notFound: number;
    errors: number;
    highConfidence: number;
    averageResponseTime: number;
    topPlatforms: string[];
  } {
    const found = results.filter(r => r.status === 'found');
    const notFound = results.filter(r => r.status === 'not_found');
    const errors = results.filter(r => r.status === 'error');
    const highConfidence = results.filter(r => r.confidence > 0.8);
    
    const avgResponseTime = results
      .filter(r => r.response_time)
      .reduce((sum, r) => sum + (r.response_time || 0), 0) / results.length;

    const topPlatforms = found
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 5)
      .map(r => r.platform);

    return {
      total: results.length,
      found: found.length,
      notFound: notFound.length,
      errors: errors.length,
      highConfidence: highConfidence.length,
      averageResponseTime: Math.round(avgResponseTime),
      topPlatforms
    };
  }
}

const usernameSearchService = new UsernameSearchService();
export default usernameSearchService;